import React from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  Info,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  ArrowLeft,
  PlugZap,
  RefreshCw,
} from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * ModuleIntelligenceCard — a unified, read-only "smart assistant" card shown on
 * a module's main page (AI-UX-001). It renders 2–4 derived insights, each with a
 * severity level and an optional recommended action, plus loading / error /
 * empty states and a standing disclaimer.
 *
 * It is purely presentational: insights are derived by the host page from data
 * it already loaded (see utils/moduleIntelligence.js). No data fetching, no
 * fabricated numbers — insights that can't be derived are passed in flagged
 * `placeholder: true` and rendered as "غير متاح — يحتاج ربط Backend".
 *
 * @param {string}  [title='المساعد الذكي']
 * @param {React.ComponentType} [icon=Sparkles]
 * @param {Array}   insights         {id,text,level,action?:{label,link},placeholder?}
 * @param {boolean} [loading=false]
 * @param {Error|string|null} [error=null]
 * @param {() => void} [onRetry]
 * @param {string}  [emptyMessage]
 * @param {boolean} [hideWhenEmpty=true]  hide the whole card when there is no
 *                                        real (non-placeholder) insight to show
 * @param {number}  [max=4]
 * @param {string}  [className]
 */

const DISCLAIMER = 'هذه توصيات تشغيلية ولا تغني عن المراجعة المتخصصة';

const LEVEL_STYLES = {
  info: { Icon: Info, dot: 'bg-blue-500', text: 'text-blue-700 dark:text-blue-300', ring: 'ring-blue-100 dark:ring-blue-900/40' },
  warning: { Icon: AlertTriangle, dot: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-300', ring: 'ring-amber-100 dark:ring-amber-900/40' },
  danger: { Icon: AlertCircle, dot: 'bg-red-500', text: 'text-red-700 dark:text-red-300', ring: 'ring-red-100 dark:ring-red-900/40' },
  success: { Icon: CheckCircle2, dot: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-300', ring: 'ring-emerald-100 dark:ring-emerald-900/40' },
  placeholder: { Icon: PlugZap, dot: 'bg-neutral-400', text: 'text-neutral-500 dark:text-neutral-400', ring: 'ring-neutral-200 dark:ring-neutral-800' },
};

const styleFor = (insight) =>
  insight.placeholder ? LEVEL_STYLES.placeholder : LEVEL_STYLES[insight.level] || LEVEL_STYLES.info;

export default function ModuleIntelligenceCard({
  title = 'المساعد الذكي',
  icon: TitleIcon = Sparkles,
  insights = [],
  loading = false,
  error = null,
  onRetry = null,
  emptyMessage = 'لا توجد مؤشرات كافية لعرضها حالياً.',
  hideWhenEmpty = true,
  max = 4,
  className = '',
}) {
  const items = Array.isArray(insights) ? insights.slice(0, max) : [];
  const realCount = items.filter((i) => !i.placeholder).length;
  const errorText = error ? error.message || String(error) : null;

  // Optional card: when nothing real can be shown (and we're settled), hide it
  // entirely rather than render an empty shell.
  if (hideWhenEmpty && !loading && !errorText && realCount === 0) return null;

  return (
    <section
      dir="rtl"
      data-testid="module-intelligence-card"
      aria-label={title}
      className={cn(
        'rounded-lg border border-violet-100 dark:border-violet-900/40 shadow-sm p-4',
        'bg-gradient-to-l from-violet-50 via-white to-primary-50 dark:from-violet-950/40 dark:via-neutral-900 dark:to-primary-950/40',
        className,
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-bold text-neutral-900 dark:text-neutral-50 flex items-center gap-2">
          <TitleIcon size={16} className="text-violet-600 dark:text-violet-400" aria-hidden="true" />
          {title}
        </h3>
        {!loading && !errorText && realCount > 0 && (
          <span className="text-[11px] text-violet-700 dark:text-violet-300 bg-violet-100 dark:bg-violet-950/60 px-2 py-0.5 rounded-full">
            {realCount}
          </span>
        )}
      </div>

      {loading && (
        <div className="space-y-2" data-testid="module-intelligence-loading" aria-busy="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-md bg-white/70 dark:bg-neutral-900/70 h-12" />
          ))}
        </div>
      )}

      {!loading && errorText && (
        <div data-testid="module-intelligence-error" className="py-2">
          <p className="text-sm text-danger-600 dark:text-danger-400">{errorText}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-violet-700 dark:text-violet-300 hover:underline"
            >
              <RefreshCw size={13} aria-hidden="true" />
              إعادة المحاولة
            </button>
          )}
        </div>
      )}

      {!loading && !errorText && items.length === 0 && (
        <p data-testid="module-intelligence-empty" className="text-sm text-neutral-500 dark:text-neutral-400 py-2">
          {emptyMessage}
        </p>
      )}

      {!loading && !errorText && items.length > 0 && (
        <ul className="space-y-2" data-testid="module-intelligence-list">
          {items.map((insight, idx) => {
            const s = styleFor(insight);
            const { Icon } = s;
            return (
              <li
                key={insight.id || idx}
                className={cn(
                  'bg-white dark:bg-neutral-900 rounded-md p-3 ring-1 flex items-start gap-2.5',
                  s.ring,
                )}
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-lg flex-shrink-0 bg-white dark:bg-neutral-800">
                  <Icon size={14} className={s.text} aria-hidden="true" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm font-medium leading-snug', s.text)}>{insight.text}</p>
                  {insight.action?.link && (
                    <Link
                      to={insight.action.link}
                      className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-violet-700 dark:text-violet-300 hover:underline"
                    >
                      {insight.action.label}
                      <ArrowLeft size={12} aria-hidden="true" />
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {!loading && !errorText && (
        <p className="mt-3 pt-2 border-t border-violet-100/70 dark:border-violet-900/30 text-[11px] text-neutral-400 dark:text-neutral-500 flex items-center gap-1">
          <Info size={11} aria-hidden="true" />
          {DISCLAIMER}
        </p>
      )}
    </section>
  );
}
